import Flex from '@Components/Bases/Flex';
import FormCheckbox from '@Components/Form/FormCheckBox';
import { defaultCompletedSection } from '@Domains/utils';
import { Box, Divider, Typography } from '@mui/material';
import { formatToShortThaiDateTime } from '@Utils/datetime';
import { useFormContext, useWatch } from 'react-hook-form';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { IGreenhouseStrategyFormValues } from '../EsgGreenhouseFormProvider/types';
import {
  checkGhStrategyCompletionEn,
  checkGhStrategyCompletionTh,
  checkGhStrategyIntroductionValidEn,
  checkGhStrategyIntroductionValidTh,
  checkGhStrategyPlanRemarkValidEn,
  checkGhStrategyPlanRemarkValidTh,
  checkGhStrategyRemarkValidEn,
  checkGhStrategyRemarkValidTh,
  checkGhStrategyUrlValid,
} from './utils';
import { resetCompletedSection } from '@Utils/updateCompletedSection';

const GreenhouseStrategyCompleteSection = () => {
  const { isPublished } = useProgressBar();
  const greenhouseStrategy = useWatch({
    name: 'greenhouseStrategy',
  }) as IGreenhouseStrategyFormValues;
  const { setValue } = useFormContext();

  const completedSection =
    greenhouseStrategy?.completedSection || defaultCompletedSection;

  const {
    isCompletedTh,
    isCompletedEn,
    dateTh,
    dateEn,
    roleStampTh,
    roleStampEn,
  } = completedSection;

  const formattedCompletedSectionDateTh = dateTh
    ? formatToShortThaiDateTime(dateTh)
    : '';
  const formattedCompletedSectionDateEn = dateEn
    ? formatToShortThaiDateTime(dateEn)
    : '';

  const isCompleteTh =
    checkGhStrategyCompletionTh(greenhouseStrategy) &&
    checkGhStrategyIntroductionValidTh(greenhouseStrategy) &&
    checkGhStrategyRemarkValidTh(greenhouseStrategy) &&
    checkGhStrategyPlanRemarkValidTh(greenhouseStrategy) &&
    checkGhStrategyUrlValid(greenhouseStrategy, 'Th');

  const isCompleteEn =
    checkGhStrategyCompletionEn(greenhouseStrategy) &&
    checkGhStrategyIntroductionValidEn(greenhouseStrategy) &&
    checkGhStrategyRemarkValidEn(greenhouseStrategy) &&
    checkGhStrategyPlanRemarkValidEn(greenhouseStrategy) &&
    checkGhStrategyUrlValid(greenhouseStrategy, 'En');

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    language: 'Th' | 'En'
  ) => {
    const updatedSection = { ...completedSection };

    if (language === 'Th') {
      updatedSection.isCompletedTh = e.target.checked;
    } else {
      updatedSection.isCompletedEn = e.target.checked;
    }

    resetCompletedSection(updatedSection, setValue, 'greenhouseStrategy');
  };

  return (
    <Box mt={2}>
      <Divider />
      <Flex gap={4} justifyContent={'center'} bgcolor={'grey.50'} py={1}>
        <Flex alignItems={'center'}>
          <Typography fontWeight="bold" textAlign={'right'}>
            ยืนยันการตรวจสอบข้อมูลครบถ้วน
          </Typography>
        </Flex>
        <Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              name="greenhouseStrategy.completedSection.isCompletedTh"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาไทย)"
              inputProps={{ disabled: isPublished || !isCompleteTh }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'Th')
              }
            />
            {isCompletedTh && dateTh && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampTh} {formattedCompletedSectionDateTh}
              </Typography>
            )}
          </Flex>

          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              sx={{ ml: 2 }}
              name="greenhouseStrategy.completedSection.isCompletedEn"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาอังกฤษ)"
              inputProps={{ disabled: isPublished || !isCompleteEn }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'En')
              }
            />
            {isCompletedEn && dateEn && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampEn} {formattedCompletedSectionDateEn}
              </Typography>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default GreenhouseStrategyCompleteSection;
